import { FC, useState } from 'react';
import styled from 'styled-components';
import laptop from '../../assets/images/laptop.jpg';
import holiday from '../../assets/images/holiday.jpg';
import homeOffice from '../../assets/images/home-office.jpg';
import note from '../../assets/images/note.jpg';
import { useTranslation } from 'react-i18next';

const ProjectSection = styled.section`
  background-color: #111;
  color: #fff;
  padding: 80px 0;
  position: relative;
  overflow: hidden;

  &:before {
    content: '</>';
    position: absolute;
    font-size: 400px;
    opacity: 0.03;
    font-family: var(--secondary-font-family);
    top: 50%;
    right: -5%;
    transform: translateY(-50%);
  }

  h2 {
    font-family: var(--secondary-font-family);
    margin-bottom: 2rem;
    
    &:before {
      content: '<';
      opacity: 0.5;
      margin-right: 0.5rem;
    }
    
    &:after {
      content: '/>';
      opacity: 0.5;
      margin-left: 0.5rem;
    }
  }
`;

const ProjectGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const ProjectCard = styled.div`
  position: relative;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.05);
  border-radius: 4px;
  overflow: hidden;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    border-color: var(--primary-color);
  }

  img {
    width: 100%;
    height: 200px;
    object-fit: cover;
    opacity: 0.7;
    transition: all 0.3s ease;
  }

  .project-info {
    padding: 1.5rem;
    
    h3 {
      font-family: var(--secondary-font-family);
      margin: 0;
      
      &:before {
        content: '_';
        color: var(--primary-color);
      }
    }

    .tech-stack {
      font-size: 0.8rem;
      color: #666;
      margin-top: 0.5rem;
    }
  }
`;

const ViewMoreButton = styled.button`
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: #fff;
  padding: 1rem 2rem;
  margin-top: 3rem;
  cursor: pointer;
  font-family: var(--secondary-font-family);
  transition: all 0.3s ease;

  &:before {
    content: '>';
    margin-right: 0.5rem;
  }

  &:hover {
    border-color: var(--primary-color);
    color: var(--primary-color);
  }
`;

const Projects: FC = () => {
  const { t } = useTranslation();
  const projects = [
    { 
      id: 1, 
      image: laptop, 
      title: t('projects.title_item1'), 
      techStack: 'React, TypeScript, Node.js',
      description: t('projects.description_item1')
    },
    { 
      id: 2, 
      image: holiday, 
      title: t('projects.title_item2'), 
      techStack: 'Next.js, Prisma, PostgreSQL',
      description: t('projects.description_item2')
    },
    { 
      id: 3, 
      image: homeOffice, 
      title: t('projects.title_item3'), 
      techStack: 'React, Redux, Material-UI',
      description: t('projects.description_item3')
    },
    { 
      id: 4, 
      image: note, 
      title: t('projects.title_item4'), 
      techStack: 'Node.js, Express, MongoDB',
      description: t('projects.description_item4')
    }
  ];

  const [visibleProjects, setVisibleProjects] = useState(4);

  const handleViewMore = () => {
    setVisibleProjects(prev => prev + 4);
  };

  return (
    <ProjectSection id="projects">
      <h2>{t('projects.title')}</h2>
      <ProjectGrid>
        {projects.slice(0, visibleProjects).map(project => (
          <ProjectCard key={project.id}>
            <img src={project.image} alt={project.title} />
            <div className="project-info">
              <h3>{project.title}</h3>
              <p className="description">{project.description}</p>
              <div className="tech-stack">{project.techStack}</div>
            </div>
          </ProjectCard>
        ))}
      </ProjectGrid>
      {visibleProjects < projects.length && (
        <div style={{ textAlign: 'center' }}>
          <ViewMoreButton onClick={handleViewMore}>
            {t('projects.action_button')}
          </ViewMoreButton>
        </div>
      )}
    </ProjectSection>
  );
};

export default Projects;